<template>
  <Toast></Toast>
  <ConfirmDialog></ConfirmDialog>

  <!-- Use v-show instead of v-if, and simplify the condition -->
  <div class="app-wrapper">
    <div v-cloak
         v-show="!shouldHideNavigation"
         class="nav-container"
         :class="{ 'hidden': !isSidebarVisible }">
      <div class="nav-logo">
        <router-link to="/" class="no-style">
          <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="22.5" cy="22.5" r="22.5" fill="#E6FF9E"/>
            <path
                d="M35.391 19.7886L33.3619 19.1875L34.8844 17.6577L33.3521 11.2475L27.212 9.42668L25.506 11.0881L24.91 8.74466L18.7687 6.92383L14.2111 11.4955L14.1854 11.5208L14.6908 13.6517L12.6605 13.0493L8.07715 17.6476L9.60819 24.0578L11.641 24.6601L10.1222 26.1912L11.6471 32.5989L17.7884 34.4197L19.4944 32.7596L20.0905 35.103L26.2305 36.9238L30.7881 32.3521L30.8138 32.3268L30.3084 30.196L32.3388 30.7983L36.8976 26.2254L36.9233 26.2001L35.3923 19.7898L35.391 19.7886ZM31.6277 12.9646L32.5872 16.9782L31.066 18.5068L27.0921 17.3287L26.1057 13.4467L27.7738 11.822L31.6277 12.9646ZM28.6464 23.7466L27.6783 19.7316L33.6666 21.5069L34.6249 25.5193L31.7562 28.3967L25.7679 26.6214L28.6464 23.7466ZM21.7941 33.3796L20.2251 27.206L28.0124 29.5152L28.5179 31.6448L25.648 34.5235L21.7941 33.3809V33.3796ZM17.3209 24.116L11.3326 22.3408L10.3743 18.3284L13.243 15.451L19.2313 17.2262L16.3773 20.0758L16.3516 20.1011L17.3197 24.116H17.3209ZM13.3715 30.8831L12.4169 26.8707L13.9345 25.3409L17.9071 26.5189L18.8935 30.401L17.2254 32.0257L13.3715 30.8831ZM26.3468 23.0645L23.4683 25.9394L19.6205 24.7993L18.6524 20.7844L21.5309 17.9095L25.3787 19.0496L26.3468 23.0645ZM23.2052 10.4681L24.7741 16.6417L16.9868 14.3324L16.4813 12.2028L19.3513 9.32418L23.2052 10.4668V10.4681Z"
                fill="#202428"/>
          </svg>
        </router-link>
      </div>
      <div class="nav-menu-items">
        <!-- Menu items rendered based on authentication status -->
        <template v-for="(item, index) in menuItems" :key="index">
          <router-link
              v-if="item.route"
              :to="item.route"
              class="nav-item"
              v-tooltip.right="item.label"
          >
            <span :class="['nav-icon', item.icon]"></span>
          </router-link>
        </template>
      </div>
      <div class="nav-auth-buttons">
        <!-- Show when authenticated -->
        <div v-if="isAuthenticated" class="profile-menu-container">
          <TieredMenu ref="profileMenu" :model="profileMenuItems" :popup="true"/>
          <button class="profile-button" @click="toggleProfileMenu">
            <Avatar :label="userInitial" class="mr-2" size="medium" shape="circle"/>
          </button>
        </div>
        <!-- Show when not authenticated -->
        <template v-else>
          <button
              class="auth-button login-button"
              @click="() => router.push('/login')"
              v-tooltip.right="'Login'"
          >
            <i class="pi pi-sign-in"></i>
          </button>
<!--          <button-->
<!--              class="auth-button register-button"-->
<!--              @click="() => router.push('/register')"-->
<!--              v-tooltip.right="'Register'"-->
<!--          >-->
<!--            <i class="pi pi-user-plus"></i>-->
<!--          </button>-->
        </template>
      </div>
    </div>

    <div class="page-container" :class="{ 'expanded': !isSidebarVisible || shouldHideNavigation }">
      <!-- Update the menu toggle to also respect navigation visibility -->
      <div class="page-top-nav">
        <div class="page-top-nav-wrapper">
          <div class="menu-toggle" v-if="!shouldHideNavigation">
            <i :class="isSidebarVisible ? 'pi pi-times' : 'pi pi-bars'" @click="toggleSidebar"></i>
          </div>
        </div>
      </div>
      <router-view/>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch, onMounted} from 'vue';
import {useStore} from 'vuex';
import {useRouter, useRoute} from 'vue-router';
import TieredMenu from 'primevue/tieredmenu';
import Avatar from 'primevue/avatar';
import {useConfirm} from "primevue/useconfirm";

const confirm = useConfirm()

const router = useRouter();
const route = useRoute();
const store = useStore();
const profileMenu = ref(null);
const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);
const isAdmin = computed(() => store.getters['auth/isAdmin'])
const isAdminOrSuperAdmin = computed(() => store.getters['auth/isAdminOrSuperAdmin'])
const isSuperAdmin = computed(() => store.getters['auth/isSuperAdmin'])
const currentUser = computed(() => store.getters['auth/currentUser']);

// Store the current route class
let currentRouteClass = null;
// Function to update the route class on #app
const updateRouteClass = (routeName) => {
  const appElement = document.getElementById('app');
  if (appElement) {
    // Remove previous route class if it exists
    if (currentRouteClass) {
      appElement.classList.remove(`route-${currentRouteClass}`);
    }

    // Add new route class if route has a name
    if (routeName) {
      const className = `route-${routeName}`;
      appElement.classList.add(className);
      currentRouteClass = routeName;
    } else {
      currentRouteClass = null;
    }
  }
};

// Watch for route changes
watch(() => route.name, (newRouteName) => {
  updateRouteClass(newRouteName);
}, { immediate: true }); // immediate: true ensures it runs on initial load

// Simplified navigation visibility check - only one computed property needed
const shouldHideNavigation = computed(() => {
  return route.meta?.hideNavigation === true;
});

// Add sidebar visibility state
const isSidebarVisible = ref(true);

// Initialize from localStorage if available, default to showing sidebar
onMounted(() => {
  const storedValue = localStorage.getItem('sidebarVisible');
  if (storedValue !== null) {
    isSidebarVisible.value = storedValue === 'true';
  } else {
    isSidebarVisible.value = true; // Default to showing sidebar
  }

  // Set initial app gap based on sidebar visibility
  updateAppGap(isSidebarVisible.value, shouldHideNavigation.value);
  // Also set the initial route class in case the watcher hasn't fired yet
  if (route.name) {
    updateRouteClass(route.name);
  }
});

// Save sidebar state to localStorage when it changes
watch(isSidebarVisible, (newValue) => {
  localStorage.setItem('sidebarVisible', newValue);
  updateAppGap(newValue, shouldHideNavigation.value);
});

// Watch for navigation visibility changes and update app gap
watch(shouldHideNavigation, (newValue) => {
  updateAppGap(isSidebarVisible.value, newValue);
});

// Function to update the app gap based on sidebar visibility and navigation visibility
const updateAppGap = (sidebarVisible, navigationHidden) => {
  const appElement = document.getElementById('app');
  if (appElement) {
    if (!sidebarVisible || navigationHidden) {
      appElement.classList.add('sidebar-hidden');
    } else {
      appElement.classList.remove('sidebar-hidden');
    }
  }
};

// Toggle sidebar visibility
const toggleSidebar = () => {
  if (!shouldHideNavigation.value) {
    isSidebarVisible.value = !isSidebarVisible.value;
  }
};

// Get user initial for avatar
const userInitial = computed(() => {
  if (currentUser.value && currentUser.value.name) {
    return currentUser.value.name.charAt(0).toUpperCase();
  }
  return 'U';
});

// Create menu items
const menuItems = ref([]);

// Profile menu items
const profileMenuItems = ref([
  {
    label: 'My Profile',
    icon: 'pi pi-user',
    command: () => router.push('/profile')
  },
  {
    label: 'Settings',
    icon: 'pi pi-cog',
    items: [
      {
        label: 'Activity Logs',
        icon: 'pi pi-history',
        command: () => router.push('/activity-logs'),
        visible: computed(() => isSuperAdmin.value)
      }
    ]
  },
  {
    separator: true
  },
  {
    label: 'Logout',
    icon: 'pi pi-sign-out',
    command: () => confirmLogout()
  }
]);

// Function to update menu items based on authentication status
const updateMenuItems = () => {
  if (isAuthenticated.value) {
    // Authenticated user menu (without logout)
    let items = [
      // {
      //   label: 'Review candidates',
      //   icon: 'pi pi-users',
      //   route: '/review-candidates/'
      // }
    ];

    // Add admin-only menu item
    if (isAdminOrSuperAdmin.value) {
      items.push(
          {
            label: 'Analytics Dashboard',
            icon: 'pi pi-chart-bar',
            route: '/analytics/'
          },
          // {
          //   label: 'Activity Logs',
          //   icon: 'pi pi-history',
          //   route: '/activity-logs/'
          // },
          {
            label: 'User Management',
            icon: 'pi pi-user-edit',
            route: '/user-management/'
          },
          {
            label: 'Talent Requests',
            icon: 'pi pi-list',
            route: '/requests/'
          },
          {
            label: 'Candidate Profiles',
            icon: 'pi pi-users',
            route: '/talents/'
          }
          // ,
          // {
          //   label: 'Compare Candidates',
          //   icon: 'pi pi-table',
          //   route: '/candidate-compare/'
          // }
      )
      ;
    }

    menuItems.value = items;
  } else {
    // Non-authenticated user menu (without login/register)
    menuItems.value = [];
  }
};

// Toggle profile menu
const toggleProfileMenu = (event) => {
  profileMenu.value.toggle(event);
};

// Logout function
const logout = () => {
  store.dispatch('auth/logout', {
    redirect: () => router.push({name: 'login'})
  });
};
const confirmLogout = () => {
  confirm.require({
    message: 'Are you sure you want to log out?',
    header: 'Logout Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => logout(),
    reject: () => {
      // User cancelled the logout action
    }
  });
};
// Watch for changes in authentication status to update menu
watch([isAuthenticated, isAdminOrSuperAdmin, isAdmin], () => {
  updateMenuItems();
}, {immediate: true});
</script>

<style lang="scss">
/* Ensure v-cloak is the first style rule */
[v-cloak] {
  display: none !important;
}

body {
  margin: 0;
  padding: 0;
}

.container {
  max-width: 100%;
  margin: 0 auto;
  padding: 1em;
}

#app {
  .app-wrapper{
    font-family: $MainFont;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: $Grey4;
    background: white;
    height: 100vh;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 1.5em;
    gap: 1.5em;
    transition: gap 0.3s ease;
    overflow-x: hidden; /* Prevent horizontal scrollbar during animation */
  }
}

/* Remove gap when sidebar is hidden */
#app.sidebar-hidden .app-wrapper{
  gap: 0;
}

.page-container {
  width: calc(100% - 80px - 1em); /* Adjust width based on sidebar width + gap */
  max-height: 100%;
  overflow: scroll;
  transition: width 0.3s ease, margin-left 0.3s ease; /* Add smooth transition */
}

/* Expanded state when sidebar is hidden */
.page-container.expanded {
  width: 100%;
  margin-left: 0;
}

/* Custom styling for the navigation */
.nav-container {
  position: sticky;
  top: 0;
  z-index: 10;
  height: 100%;
  width: 80px; /* Fixed width for sidebar */
  min-width: 80px; /* Prevent shrinking */
  padding: 1em 1em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: $Grey15;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 1em;
  transition: all 0.3s ease-in-out;

  // When hidden via v-show, ensure it's completely invisible
  &:not([style*="display: none"]) {
    visibility: visible;
    opacity: 1;
  }
}

/* Hidden state styling for sidebar toggle */
.nav-container.hidden {
  transform: translateX(-120%); /* Move off-screen */
  opacity: 0;
  margin-left: -80px; /* Pull out of the flex layout */
  pointer-events: none; /* Prevent interactions while hidden */
}

.nav-logo {
  margin-bottom: 2em;
}

.nav-logo svg {
  height: 45px;
  width: auto;
}

.nav-menu-items {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1em;
  margin-bottom: 0.5em;
  color: $Grey12;
  text-decoration: none;
  font-weight: 500;
  border-radius: 0.5em;
  transition: all 0.2s ease;
  justify-content: center;
}

.nav-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.nav-item.router-link-active {
  background-color: white;
  color: black;
}

.nav-icon {
}

.nav-label {
  font-size: 0.9em;
}

.nav-auth-buttons {
  margin-top: 2em;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.auth-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1em;
  margin-bottom: 0.5em;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  color: $Grey12;
  width: 100%;
  transition: all 0.2s ease;
}

.auth-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.auth-button i {
}

.profile-button {
  background-color: transparent;
  box-shadow: none !important;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  padding: 0;
  align-items: center;
  justify-content: center;

  .p-avatar {
    width: 42px;
    height: 42px;
  }
}

.profile-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.profile-menu-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.no-style {
  text-decoration: none;
}

/* Menu toggle styling */
.menu-toggle {
  display: none;
  cursor: pointer;
  position: absolute;
  top: 0;
  left: 0;
}

.placeholder {
  display: none;
}

.page-logo {
  svg {
    height: 25px;
  }
}


@media (max-width: 768px) {
  .menu-toggle {
    display: block;
  }
  .placeholder {
    display: block;
  }
}

.menu-toggle i {
  font-size: 1.5em;
  transition: transform 0.3s ease;
}

.placeholder {
  width: 1.5em;
}

.menu-toggle i:hover {
  transform: scale(1.1);
}

/* Page top nav styling */
.page-top-nav {
  transition: padding 0.3s ease;
}

@media (max-width: 768px) {
  .page-top-nav {
    padding: 0em 0 1em;
  }
  .container {
    padding: 2em 0 1em;
  }
}

.page-top-nav-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

@media (max-width: 768px) {
  .page-top-nav-wrapper {
    //justify-content: space-between;
  }
}


/* Adjust page title when sidebar is expanded/collapsed */
.page-container.expanded .menu-toggle {
  display: block;
}

.page-container.expanded .placeholder {
  display: block;
}
</style>
